/**
 * Location-local timestamp formatting for the consent evidence report
 * (feat-022-consent-evidence, spec D8).
 *
 * Every timestamp in the consent evidence PDF must render in the location's
 * local timezone with an explicit abbreviation + UTC offset so the document is
 * unambiguous (subpoena-grade) — e.g. `2026-04-12 09:31 AM PDT (UTC−07:00)`.
 *
 * Reuses the existing `date-fns-tz` `formatInTimeZone` pattern
 * (see `RecentShiftsTable.tsx:257`). Backend stores `occurred_at` /
 * `consent_given_at` as UTC; DB datetime strings arrive without a zone suffix,
 * so we treat a bare string as UTC (append `Z`) exactly like RecentShiftsTable.
 */

import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

/** Per D8: when `location.timezone` is null, fall back to UTC, labeled UTC. */
export const FALLBACK_TIMEZONE = 'UTC';

const UNICODE_MINUS = '−'; // − (matches the spec example UTC−07:00)

export interface LocationTimeParts {
  /** Local wall-clock, e.g. `2026-04-12 09:31 AM`. */
  dateTime: string;
  /** Zone abbreviation, e.g. `PDT`, `EST`, `UTC`. */
  abbreviation: string;
  /** UTC offset label, e.g. `UTC−07:00` (unicode minus, `UTC+00:00` for UTC). */
  offsetLabel: string;
  /** Full labeled stamp, e.g. `2026-04-12 09:31 AM PDT (UTC−07:00)`. */
  full: string;
}

/**
 * Coerce a backend datetime string to a `Date`, treating a zone-less string as
 * UTC (the backend stores UTC but MySQL emits no suffix). Strings that already
 * carry `Z` or a numeric offset are left as-is.
 */
function toUtcDate(dateString: string): Date {
  const hasZone = dateString.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateString);
  return parseISO(hasZone ? dateString : `${dateString}Z`);
}

/**
 * Format a UTC datetime string in a location's IANA timezone with an explicit
 * abbreviation + UTC offset. Returns `null` for empty input or an unparseable
 * date so callers can render a "—" / "Not recorded" placeholder.
 *
 * @param dateString UTC datetime (ISO or MySQL `YYYY-MM-DD HH:MM:SS`); zone-less treated as UTC.
 * @param timezone   IANA zone (e.g. `America/Los_Angeles`); null/empty → UTC (D8 fallback).
 */
export function formatLocationTimeParts(
  dateString: string | null | undefined,
  timezone: string | null | undefined,
): LocationTimeParts | null {
  if (!dateString) return null;
  const tz = timezone || FALLBACK_TIMEZONE;
  try {
    const date = toUtcDate(dateString);
    if (Number.isNaN(date.getTime())) return null;
    const dateTime = formatInTimeZone(date, tz, 'yyyy-MM-dd hh:mm a');
    const abbreviation = formatInTimeZone(date, tz, 'zzz');
    const rawOffset = formatInTimeZone(date, tz, 'xxx'); // "-07:00" | "+00:00"
    const offsetLabel = `UTC${rawOffset.replace('-', UNICODE_MINUS)}`;
    return {
      dateTime,
      abbreviation,
      offsetLabel,
      full: `${dateTime} ${abbreviation} (${offsetLabel})`,
    };
  } catch {
    return null;
  }
}

/**
 * Convenience: the full labeled stamp, or `fallbackLabel` (default `—`) when
 * the input is empty/unparseable. Use directly in table cells.
 */
export function formatLocationTime(
  dateString: string | null | undefined,
  timezone: string | null | undefined,
  fallbackLabel = '—',
): string {
  return formatLocationTimeParts(dateString, timezone)?.full ?? fallbackLabel;
}
